import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Sessions } from "@app/shared/src/api/api.gen";
import { Btn, TextInput } from "@app/shared/src/components_v2";
import { AuthFrame, AuthLabel, AuthLinks, AuthPanel } from "../layouts/AuthFrame";

/**
 * Ask for a password reset link.
 *
 * The server answers the same way whether or not the address has an account — saying otherwise
 * would let anyone check who is a customer — so the confirmation here is worded to match. The
 * link in the email opens the reset page on the main Omnisight dashboard, which shares the
 * account; the new password works here the moment it is set.
 */
export const ForgotPasswordPage: React.FC = () => {
    const location = useLocation() as { state?: { email?: string } };
    const [email, setEmail] = useState(location.state?.email ?? "");
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState<string>();

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        setError(undefined);
        try {
            await Sessions.forgotPassword(email.trim());
            setSent(true);
        } catch (err) {
            setError(err instanceof Error && err.message ? err.message : "Could not send the reset email. Try again in a minute.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <AuthFrame>
            <div className="mb-5">
                <h1 className="text-[22px] font-bold tracking-tight">Forgot your password?</h1>
                <p className="mt-1.5 text-[13.5px] leading-relaxed text-[var(--muted)]">
                    Enter the email you sign in with and we'll send a link to choose a new one.
                </p>
            </div>

            {sent ? (
                <div className="rounded-lg border border-[var(--line)] bg-[var(--surface)] p-5 text-[13.5px] leading-relaxed">
                    <p>
                        If <strong>{email.trim()}</strong> has an Omnisight account, a reset link is on its way. It's good for a short
                        while, so check your inbox — and the junk folder — now.
                    </p>
                    <Link to="/login" state={{ email: email.trim() }} className="mt-4 block">
                        <Btn type="button" size="lg" className="w-full">
                            Back to sign in
                        </Btn>
                    </Link>
                </div>
            ) : (
                <AuthPanel onSubmit={submit}>
                    <AuthLabel>Email</AuthLabel>
                    <TextInput
                        size="lg"
                        type="email"
                        autoComplete="email"
                        required
                        autoFocus
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mb-4"
                    />
                    {error && <p className="mb-3 text-[12.5px] text-[var(--crit)]">{error}</p>}
                    <Btn type="submit" size="lg" disabled={busy || !email.trim()} className="w-full">
                        {busy ? "Sending…" : "Send reset link"}
                    </Btn>
                </AuthPanel>
            )}

            <AuthLinks>
                <Link to="/login" state={{ email: email.trim() }} className="hover:text-[var(--text)]">
                    Back to sign in
                </Link>
                <Link to="/create-account" className="hover:text-[var(--text)]">
                    Create a new account
                </Link>
            </AuthLinks>
        </AuthFrame>
    );
};
